import React, { useState } from "react";
import Papa from "papaparse";
import { endpoints } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Upload, FileText, Check, X, Download } from "lucide-react";
import { toast } from "sonner";

const FIELD_OPTIONS = {
  member: [
    { key: "_skip", label: "- skip column -" },
    { key: "name", label: "Name *" },
    { key: "phone", label: "Phone *" },
    { key: "email", label: "Email" },
    { key: "plan", label: "Plan name" },
    { key: "plan_amount", label: "Plan amount" },
    { key: "start_date", label: "Start date" },
    { key: "expiry_date", label: "Expiry date" },
    { key: "last_visit", label: "Last visit" },
  ],
  lead: [
    { key: "_skip", label: "- skip column -" },
    { key: "name", label: "Name *" },
    { key: "phone", label: "Phone *" },
    { key: "source", label: "Source" },
    { key: "interest", label: "Interest" },
  ],
};

const TEMPLATES = {
  member: {
    filename: "fitnudge-members-template.csv",
    headers: ["Name", "Phone", "Email", "Plan", "Amount", "Start date", "Expiry date", "Last visit"],
    rows: [
      ["Riya Kapoor", "+919876500201", "riya@example.com", "Monthly", "2000", "2026-01-01", "2026-02-01", "2026-01-28"],
      ["Aman Joshi", "+919876500202", "", "Quarterly", "5400", "2025-12-15", "2026-03-15", ""],
    ],
  },
  lead: {
    filename: "fitnudge-leads-template.csv",
    headers: ["Name", "Phone", "Source", "Interest"],
    rows: [
      ["Rohan Mehra", "+919876500301", "Instagram", "Boxing"],
      ["Anjali Singh", "+919876500302", "Walk-in", "Strength"],
    ],
  },
};

function guessMapping(header, kind) {
  const h = (header || "").toLowerCase().trim();
  if (["name", "full name", "member name", "lead name"].includes(h)) return "name";
  if (["phone", "phone number", "mobile", "contact"].includes(h)) return "phone";
  if (kind === "member") {
    if (["email", "e-mail"].includes(h)) return "email";
    if (["plan", "plan name", "membership"].includes(h)) return "plan";
    if (["amount", "plan amount", "fee", "price"].includes(h)) return "plan_amount";
    if (["start", "start date", "join date", "joined"].includes(h)) return "start_date";
    if (["expiry", "expiry date", "end date", "end"].includes(h)) return "expiry_date";
    if (["last visit", "last_visit", "lastvisit"].includes(h)) return "last_visit";
  }
  if (kind === "lead") {
    if (["source", "channel"].includes(h)) return "source";
    if (["interest", "interested in", "service"].includes(h)) return "interest";
  }
  return "_skip";
}

function downloadTemplate(kind) {
  const t = TEMPLATES[kind];
  const csv = Papa.unparse({ fields: t.headers, data: t.rows });
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = t.filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function CsvImportDialog({ kind = "member", onImported, triggerLabel, triggerTestId }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [duration, setDuration] = useState(30);

  const fields = FIELD_OPTIONS[kind];

  const reset = () => {
    setRows([]); setHeaders([]); setMapping({}); setSubmitting(false);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: (res) => {
        const data = res.data;
        if (!data.length) { toast.error("Empty CSV"); return; }
        const first = data[0].map(c => String(c).trim());
        const looksLikeHeader = first.some(c => isNaN(parseFloat(c)) && !/^\+?\d/.test(c));
        if (looksLikeHeader) {
          setHeaders(first);
          setRows(data.slice(1));
          const auto = {};
          first.forEach((h, i) => { const g = guessMapping(h, kind); if (g !== "_skip") auto[i] = g; });
          setMapping(auto);
        } else {
          const hdrs = first.map((_, i) => `Column ${i + 1}`);
          setHeaders(hdrs);
          setRows(data);
          setMapping({});
        }
      },
      error: () => toast.error("Failed to parse CSV"),
    });
  };

  const parseDate = (v) => {
    if (!v) return undefined;
    const d = new Date(v);
    return isNaN(d.getTime()) ? undefined : d.toISOString();
  };
  const parseNumber = (v) => {
    if (v === "" || v === null || v === undefined) return undefined;
    const n = parseFloat(String(v).replace(/[^\d.]/g, ""));
    return isNaN(n) ? undefined : n;
  };

  const doImport = async () => {
    const values = Object.values(mapping);
    if (!values.includes("name") || !values.includes("phone")) {
      toast.error("Map both Name and Phone columns");
      return;
    }
    setSubmitting(true);
    try {
      const payload = rows.map(r => {
        const o = {};
        Object.entries(mapping).forEach(([colIdx, field]) => {
          if (!field || field === "_skip") return;
          const raw = r[colIdx];
          if (raw === undefined || raw === null || String(raw).trim() === "") return;
          if (field === "plan_amount") o[field] = parseNumber(raw);
          else if (["start_date", "expiry_date", "last_visit"].includes(field)) o[field] = parseDate(raw);
          else o[field] = String(raw).trim();
        });
        return o;
      }).filter(o => o.name && o.phone);

      if (payload.length === 0) {
        toast.error("No valid rows to import");
        setSubmitting(false);
        return;
      }

      const body = kind === "member"
        ? { rows: payload, default_duration_days: parseInt(duration) || 30 }
        : { rows: payload };
      const fn = kind === "member" ? endpoints.importMembers : endpoints.importLeads;
      const { data } = await fn(body);
      toast.success(`Imported ${data.created} ${kind}${data.created === 1 ? "" : "s"}`, {
        description: data.errors?.length ? `${data.errors.length} rows skipped` : undefined,
      });
      onImported?.();
      setOpen(false);
      reset();
    } catch {
      toast.error("Import failed");
    } finally {
      setSubmitting(false);
    }
  };

  const label = triggerLabel || `Import ${kind === "lead" ? "leads" : "members"} CSV`;

  // Ref for the hidden <input type="file"> so a proper button can trigger it
  // without wrapping the whole drop-zone in a <label> (which caused stray
  // clicks on the inline "Download template" link to also trigger the file
  // picker and vice-versa).
  const fileInputRef = React.useRef(null);
  const openPicker = () => fileInputRef.current?.click();

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <button
          data-testid={triggerTestId || `csv-import-${kind}-btn`}
          className="inline-flex items-center gap-2 bg-card border border-border hover:bg-muted/50 text-foreground font-medium px-4 py-2.5 rounded-lg transition-colors"
        >
          <Upload className="w-4 h-4" /> Import CSV
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center justify-between gap-4">
            <span>{label}</span>
            <button
              type="button"
              onClick={() => downloadTemplate(kind)}
              data-testid={`download-template-${kind}`}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 hover:bg-primary/10 px-2.5 py-1 rounded-md transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Download template
            </button>
          </DialogTitle>
        </DialogHeader>

        {/* Hidden file input - triggered explicitly by the button below */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          data-testid={`csv-file-input-${kind}`}
          onChange={onFile}
          className="hidden"
        />

        {rows.length === 0 ? (
          <div className="py-6">
            <div className="border-2 border-dashed border-border rounded-xl p-10 text-center bg-muted/10">
              <FileText className="w-8 h-8 mx-auto text-muted-foreground/70 mb-3" />
              <div className="text-sm font-semibold text-foreground mb-1">Upload a CSV file</div>
              <div className="text-xs text-muted-foreground mb-5 max-w-md mx-auto">
                Any column order works. You&apos;ll map columns to fields in the next step.
              </div>
              <button
                type="button"
                onClick={openPicker}
                data-testid={`csv-choose-file-${kind}`}
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
              >
                <Upload className="w-4 h-4" /> Choose CSV file
              </button>
              <div className="mt-5 text-[11px] text-muted-foreground/70">
                No file yet? Use the <span className="text-primary font-medium">Download template</span> link above for a starting sample.
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="text-xs text-muted-foreground mb-3">
              Detected {rows.length} row{rows.length === 1 ? "" : "s"} · {headers.length} column{headers.length === 1 ? "" : "s"}. Map each column to a field.
            </div>
            <div className="border border-border rounded-lg overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-muted/30">
                  <tr>
                    {headers.map((h, i) => (
                      <th key={i} className="text-left p-2 font-semibold text-foreground/85 border-b border-border">
                        <div className="truncate mb-1 text-muted-foreground text-[10px] uppercase tracking-wider">{h}</div>
                        <Select
                          value={mapping[i] || "_skip"}
                          onValueChange={(v) => setMapping({ ...mapping, [i]: v === "_skip" ? "" : v })}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid={`map-col-${i}`}>
                            <SelectValue placeholder="Skip" />
                          </SelectTrigger>
                          <SelectContent>
                            {fields.map(o => (
                              <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 5).map((r, ri) => (
                    <tr key={ri} className="border-b border-border/60">
                      {r.map((c, ci) => (
                        <td key={ci} className="p-2 text-foreground/85 align-top truncate max-w-[160px]">{c}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > 5 && (
                <div className="p-2 text-center text-[11px] text-muted-foreground/70 bg-muted/30">
                  + {rows.length - 5} more rows
                </div>
              )}
            </div>

            {kind === "member" && (
              <div className="mt-4 flex items-center gap-3 text-xs">
                <label className="text-muted-foreground">Default duration (days) if no expiry in CSV:</label>
                <input
                  type="number"
                  value={duration}
                  onChange={e => setDuration(e.target.value)}
                  className="w-20 border border-border rounded-md px-2 py-1 text-xs bg-card"
                  data-testid="default-duration-input"
                />
              </div>
            )}
          </>
        )}

        <DialogFooter>
          {rows.length > 0 && (
            <>
              <button
                onClick={reset}
                className="text-sm text-muted-foreground hover:text-foreground px-3 py-2 rounded-lg"
              >
                <X className="w-4 h-4 inline mr-1" /> Reset
              </button>
              <button
                onClick={doImport}
                disabled={submitting}
                data-testid="csv-import-submit"
                className="bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground font-medium px-4 py-2 rounded-lg transition-colors inline-flex items-center gap-1.5"
              >
                <Check className="w-4 h-4" /> {submitting ? "Importing…" : `Import ${rows.length} row${rows.length === 1 ? "" : "s"}`}
              </button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
